import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { seo } from "@/utils/seo";
import { createFileRoute } from "@tanstack/react-router";
import {
  Activity,
  AlertTriangle,
  Baby,
  CheckCircle,
  Droplets,
  Lightbulb,
  Target,
  Thermometer,
  XCircle,
} from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_app/essay/water-calculator")({
  component: WaterIntakeCalculator,
  head: () => ({
    meta: seo({
      title: "Su İhtiyacı Hesaplayıcı - Günlük Su Tüketimi | AtalayTheCoach",
      description:
        "Kişiselleştirilmiş günlük su ihtiyacınızı hesaplayın. Aktivite, iklim ve kiloya göre optimum hidrasyon rehberi.",
      keywords:
        "su ihtiyacı hesaplama, günlük su tüketimi, hidrasyon rehberi, spor su ihtiyacı, Alican Atalay",
      canonical: "https://atalaythecoach.com/essay/water-calculator",
    }),
  }),
});

// Temel Su İhtiyacı Hesaplama
// Kaynak: IOM/EFSA kılavuzları + klinik beslenme pratiği (ml/kg yöntemi)
// IOM erkekler için ~3.0L/gün içecek hedefine daha yakın: 38ml/kg
// IOM kadınlar için ~2.2L/gün içecek hedefine daha yakın: 33ml/kg
function calculateBaseWaterNeed(weight: number, gender: string): number {
  const baseMultiplier = gender === "male" ? 38 : 33;
  return (weight * baseMultiplier) / 1000; // Litre cinsinden
}

// Aktivite Seviyesi İlave Su Miktarı (Litre)
const activityMultipliers = {
  sedentary: {
    addition: 0.0,
    label: "Hareketsiz",
    desc: "Masa başı iş, hiç spor yapmıyor",
  },
  light: {
    addition: 0.35,
    label: "Az Aktif",
    desc: "Hafif aktivite, haftada 1-3 gün egzersiz (+350ml)",
  },
  moderate: {
    addition: 0.7,
    label: "Orta Aktif",
    desc: "Orta düzey aktivite, haftada 3-5 gün egzersiz (+700ml)",
  },
  high: {
    addition: 1.1,
    label: "Çok Aktif",
    desc: "Yoğun aktivite, haftada 6-7 gün egzersiz (+1.1L)",
  },
  extreme: {
    addition: 1.5,
    label: "Süper Aktif",
    desc: "Çok yoğun aktivite veya fiziksel iş (+1.5L)",
  },
};

// İklim İlave Su Miktarı (Litre)
const climateMultipliers = {
  cold: {
    addition: -0.2,
    label: "Soğuk İklim",
    desc: "0-15°C, daha az terleme (-200ml)",
  },
  temperate: {
    addition: 0.0,
    label: "Ilıman İklim",
    desc: "15-25°C, normal koşullar",
  },
  hot: {
    addition: 0.5,
    label: "Sıcak İklim",
    desc: "25-35°C, artan terleme (+500ml)",
  },
  extreme: {
    addition: 1.0,
    label: "Çok Sıcak İklim",
    desc: "35°C+, aşırı terleme (+1.0L)",
  },
};

// Yaş İlave Su Miktarı (Litre)
function getAgeAddition(age: number): number {
  if (age < 19) return 0.2; // Gençler için artırılmış ihtiyaç (+200ml)
  if (age >= 65) return 0.2; // Yaşlılar için artırılmış ihtiyaç (+200ml)
  return 0.0;
}

// Özel Durumlar İlave Su Miktarı (Litre)
const specialConditions = {
  none: { addition: 0.0, label: "Yok", desc: "Normal koşullar" },
  pregnancy: {
    addition: 0.3,
    label: "Hamilelik",
    desc: "Artan sıvı ihtiyacı (+300ml)",
  },
  breastfeeding: {
    addition: 0.7,
    label: "Emzirme",
    desc: "Süt üretimi için ekstra sıvı (+700ml)",
  },
  illness: {
    addition: 0.6,
    label: "Hastalık",
    desc: "Ateş, kusma, ishal durumları (+600ml)",
  },
  medication: {
    addition: 0.3,
    label: "İlaç Kullanımı",
    desc: "Diüretik etkili ilaçlar (+300ml)",
  },
};

// Hidrasyon Seviyeleri
const getHydrationLevel = (intake: number, need: number) => {
  const ratio = intake / need;

  if (ratio < 0.4) {
    return {
      level: "Ciddi Dehidrasyon",
      color: "bg-destructive text-destructive-foreground",
      desc: "Sıvı tüketiminiz çok yetersiz. Sağlığınız için su alımınızı acilen artırmalısınız.",
    };
  } else if (ratio < 0.7) {
    return {
      level: "Düşük Hidrasyon",
      color: "bg-destructive text-destructive-foreground",
      desc: "Günlük sıvı alımınız hedefinizin altında. Su tüketiminizi kademeli olarak artırmayı deneyin.",
    };
  } else if (ratio < 0.85) {
    return {
      level: "Orta Hidrasyon",
      color: "bg-warning text-warning-foreground",
      desc: "Hedefinize yaklaşmak için su tüketiminizi biraz artırabilirsiniz.",
    };
  } else if (ratio >= 0.85 && ratio <= 1.2) {
    return {
      level: "Optimal Hidrasyon",
      color: "bg-primary text-primary-foreground",
      desc: "Mükemmel hidrasyon seviyesi.",
    };
  } else {
    return {
      level: "Yüksek Hidrasyon",
      color: "bg-secondary text-secondary-foreground",
      desc: "Günlük ihtiyacınızı fazlasıyla karşılıyorsunuz.",
    };
  }
};

function WaterIntakeCalculator() {
  const [gender, setGender] = useState<string>("");
  const [age, setAge] = useState<string>("");
  const [weight, setWeight] = useState<string>("");
  const [activityLevel, setActivityLevel] = useState<string>("");
  const [climate, setClimate] = useState<string>("");
  const [specialCondition, setSpecialCondition] = useState<string>("none");
  const [currentIntake, setCurrentIntake] = useState<string>("");
  const [results, setResults] = useState<{
    baseNeed: number;
    totalNeed: number;
    hourlyIntake: number;
    cupCount: number;
    hydrationLevel?: any;
  } | null>(null);

  const calculateWaterNeed = () => {
    const weightNum = Number.parseFloat(weight);
    const ageNum = Number.parseFloat(age);
    const currentIntakeNum = Number.parseFloat(currentIntake) || 0;

    if (!weightNum || !ageNum || !gender || !activityLevel || !climate) return;

    // Temel su ihtiyacı
    const baseNeed = calculateBaseWaterNeed(weightNum, gender);

    // İlave su miktarlarını uygula
    const activityAddition =
      activityMultipliers[activityLevel as keyof typeof activityMultipliers]
        .addition;
    const climateAddition =
      climateMultipliers[climate as keyof typeof climateMultipliers].addition;
    const ageAddition = getAgeAddition(ageNum);
    const specialAddition =
      specialConditions[specialCondition as keyof typeof specialConditions]
        .addition;

    // Toplam su ihtiyacı (toplamsal yaklaşım)
    const totalNeed =
      baseNeed +
      activityAddition +
      climateAddition +
      ageAddition +
      specialAddition;

    // Saatlik ve bardak hesaplaması
    const hourlyIntake = totalNeed / 16; // 16 saatlik uyanık süre
    const cupCount = Math.round(totalNeed / 0.25); // 250ml bardak

    // Mevcut hidrasyon seviyesi
    const hydrationLevel =
      currentIntakeNum > 0
        ? getHydrationLevel(currentIntakeNum, totalNeed)
        : null;

    setResults({
      baseNeed,
      totalNeed,
      hourlyIntake,
      cupCount,
      hydrationLevel,
    });
  };

  return (
    <div className="min-h-screen bg-background pt-32 pb-16">
      <div className="container mx-auto max-w-5xl px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/20">
              <Droplets className="h-12 w-12 text-primary" />
            </div>
          </div>
          <h1 className="mb-6 font-bold text-4xl text-foreground leading-tight md:text-5xl lg:text-6xl">
            Su İhtiyacı Hesaplayıcı
          </h1>
          <p className="mx-auto max-w-3xl text-lg text-muted-foreground leading-relaxed md:text-xl">
            Kişiselleştirilmiş günlük su ihtiyacınızı hesaplayın. Optimal
            hidrasyon için bilimsel rehberlik alın.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Hesaplayıcı Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Droplets className="h-5 w-5 text-primary" />
                  Su İhtiyacı Hesapla
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Temel Bilgiler */}
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-3">
                    <Label>Cinsiyet</Label>
                    <RadioGroup value={gender} onValueChange={setGender}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="male" id="male-water" />
                        <Label htmlFor="male-water">Erkek</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="female" id="female-water" />
                        <Label htmlFor="female-water">Kadın</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="age">Yaş</Label>
                    <Input
                      id="age"
                      type="number"
                      placeholder="25"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="weight">Kilo (kg)</Label>
                    <Input
                      id="weight"
                      type="number"
                      placeholder="70"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                    />
                  </div>
                </div>

                {/* Aktivite Seviyesi */}
                <div className="space-y-3">
                  <Label>Aktivite Seviyesi</Label>
                  <Select
                    value={activityLevel}
                    onValueChange={setActivityLevel}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Aktivite seviyenizi seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(activityMultipliers).map(
                        ([key, activity]) => (
                          <SelectItem key={key} value={key}>
                            <div>
                              <div className="font-medium">
                                {activity.label}
                              </div>
                              <div className="text-muted-foreground text-xs">
                                {activity.desc}
                              </div>
                            </div>
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* İklim Koşulları */}
                <div className="space-y-3">
                  <Label>İklim Koşulları</Label>
                  <Select value={climate} onValueChange={setClimate}>
                    <SelectTrigger>
                      <SelectValue placeholder="İklim koşullarını seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(climateMultipliers).map(
                        ([key, climate]) => (
                          <SelectItem key={key} value={key}>
                            <div>
                              <div className="font-medium">{climate.label}</div>
                              <div className="text-muted-foreground text-xs">
                                {climate.desc}
                              </div>
                            </div>
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Özel Durumlar */}
                <div className="space-y-3">
                  <Label>Özel Durumlar</Label>
                  <Select
                    value={specialCondition}
                    onValueChange={setSpecialCondition}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Özel durumları seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(specialConditions).map(
                        ([key, condition]) => (
                          <SelectItem key={key} value={key}>
                            <div>
                              <div className="font-medium">
                                {condition.label}
                              </div>
                              <div className="text-muted-foreground text-xs">
                                {condition.desc}
                              </div>
                            </div>
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Mevcut Su Tüketimi */}
                <div className="space-y-2">
                  <Label htmlFor="currentIntake">
                    Mevcut Günlük Su Tüketiminiz (L) - Opsiyonel
                  </Label>
                  <Input
                    id="currentIntake"
                    type="number"
                    placeholder="2.5"
                    step="0.1"
                    value={currentIntake}
                    onChange={(e) => setCurrentIntake(e.target.value)}
                  />
                  <p className="text-muted-foreground text-xs">
                    Mevcut hidrasyon durumunuzu değerlendirmek için günlük su
                    tüketiminizi girebilirsiniz
                  </p>
                </div>

                <Button
                  onClick={calculateWaterNeed}
                  className="w-full"
                  disabled={
                    !weight || !age || !gender || !activityLevel || !climate
                  }
                >
                  Su İhtiyacını Hesapla
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Sonuçlar */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Sonuçlarınız
                </CardTitle>
              </CardHeader>
              <CardContent>
                {results ? (
                  <div className="space-y-6">
                    <div className="rounded-lg bg-primary/10 p-6 text-center">
                      <div className="mb-2 font-bold text-4xl text-primary">
                        {results.totalNeed.toFixed(1)}L
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Günlük Su İhtiyacı
                      </div>
                      <div className="mt-1 text-muted-foreground text-xs">
                        (Temel: {results.baseNeed.toFixed(1)}L)
                      </div>
                    </div>

                    {results.hydrationLevel && (
                      <div className="space-y-3">
                        <h4 className="font-semibold">Mevcut Hidrasyon:</h4>
                        <div
                          className={`rounded-lg p-3 text-center ${results.hydrationLevel.color}`}
                        >
                          <div className="font-bold">
                            {results.hydrationLevel.level}
                          </div>
                          <div className="mt-1 text-xs">
                            {results.hydrationLevel.desc}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3">
                      <h4 className="font-semibold">Pratik Rehber:</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between rounded bg-secondary/10 p-2 dark:bg-secondary/20">
                          <span>Saatlik su:</span>
                          <span className="font-bold">
                            {(results.hourlyIntake * 1000).toFixed(0)}ml
                          </span>
                        </div>
                        <div className="flex items-center justify-between rounded bg-primary/10 p-2 dark:bg-primary/20">
                          <span>Bardak sayısı:</span>
                          <span className="font-bold">
                            {results.cupCount} bardak
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold">Su İçme Takvimi:</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {[
                          {
                            time: "07:00",
                            ratio: 0.2,
                            desc: "Uyanır uyanmaz",
                          },
                          {
                            time: "09:00",
                            ratio: 0.1,
                            desc: "Kahvaltı sonrası",
                          },
                          { time: "11:00", ratio: 0.1, desc: "Ara öğün" },
                          {
                            time: "13:00",
                            ratio: 0.15,
                            desc: "Öğle yemeği",
                          },
                          {
                            time: "15:00",
                            ratio: 0.1,
                            desc: "Öğleden sonra",
                          },
                          { time: "17:00", ratio: 0.1, desc: "Akşamüstü" },
                          {
                            time: "19:00",
                            ratio: 0.15,
                            desc: "Akşam yemeği",
                          },
                          {
                            time: "21:00",
                            ratio: 0.1,
                            desc: "Gece öncesi",
                          },
                        ].map((schedule) => {
                          const amountMl =
                            Math.round(
                              (results.totalNeed * 1000 * schedule.ratio) / 50,
                            ) * 50;
                          return (
                            <div
                              key={schedule.time}
                              className="rounded bg-muted p-2"
                            >
                              <div className="font-semibold">
                                {schedule.time}
                              </div>
                              <div className="font-bold text-primary">
                                {amountMl}ml
                              </div>
                              <div className="text-muted-foreground">
                                {schedule.desc}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="space-y-2 rounded border border-info/20 bg-info/5 p-4 dark:border-info/30 dark:bg-info/10">
                      <p className="text-info-foreground text-sm leading-relaxed">
                        <strong>💡 Bilgi:</strong> Günlük sıvı ihtiyacımızın
                        yaklaşık %20'si yediğimiz besinlerden (meyve, sebze,
                        yemekler) karşılanır. Hesaplanan miktar toplam sıvı
                        ihtiyacınızdır.
                      </p>
                      <p className="text-info-foreground text-sm leading-relaxed">
                        <strong>⚠️ Not:</strong> Bu hesaplama genel rehberlik
                        içindir. Tıbbi durumlar veya kronik rahatsızlıklar
                        (böbrek, kalp vb.) için mutlaka doktor görüşü alın.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    Hesaplama yapmak için soldaki formu doldurun
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Modern Bilgilendirici İçerik */}
        <div className="mt-16 space-y-12">
          {/* Hero Section */}
          <div className="rounded-2xl border border-border/50 bg-card p-8 md:p-12">
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <h2 className="mb-4 font-bold text-3xl text-foreground">
                  Hidrasyonun Bilimini Keşfedin
                </h2>
                <p className="mb-6 text-lg text-muted-foreground">
                  Vücudunuzun %60'ı su! Optimal performans ve sağlık için
                  kişiselleştirilmiş hidrasyon planınızı oluşturun.
                </p>
              </div>
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/20">
                    <Droplets className="h-12 w-12 text-primary" />
                  </div>
                </div>
                <div className="text-muted-foreground text-sm">
                  Bilimsel hesaplama ile doğru hidrasyon
                </div>
              </div>
            </div>
          </div>

          {/* Dehydration Signs */}
          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <AlertTriangle className="h-8 w-8 text-destructive" />
                </div>
                <h3 className="font-bold text-destructive text-xl">
                  Dehidrasyon Belirtileri
                </h3>
              </div>
              <ul className="space-y-2 text-foreground text-sm">
                <li className="flex items-center gap-2">
                  <span className="text-destructive">●</span>
                  <span>Ağız kuruluğu</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-destructive">●</span>
                  <span>Koyu sarı idrar</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-destructive">●</span>
                  <span>Baş ağrısı</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-destructive">●</span>
                  <span>Yorgunluk</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-destructive">●</span>
                  <span>Konsantrasyon bozukluğu</span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <CheckCircle className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-primary text-xl">
                  Optimal Hidrasyon
                </h3>
              </div>
              <ul className="space-y-2 text-foreground text-sm">
                <li className="flex items-center gap-2">
                  <span className="text-primary">●</span>
                  <span>Açık sarı idrar</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">●</span>
                  <span>Nemli ağız</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">●</span>
                  <span>Yüksek enerji</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">●</span>
                  <span>Net zihinsel odak</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">●</span>
                  <span>İyi fiziksel performans</span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <XCircle className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-primary text-xl">
                  Aşırı Hidrasyon
                </h3>
              </div>
              <ul className="space-y-2 text-foreground text-sm">
                <li className="flex items-center gap-2">
                  <span className="text-primary">●</span>
                  <span>Çok açık/berrak idrar</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">●</span>
                  <span>Sık idrara çıkma</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">●</span>
                  <span>Mide bulantısı</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">●</span>
                  <span>Elektrolit dengesizliği</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary">●</span>
                  <span>Hiponatremi riski</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Water Sources */}
          <div className="rounded-2xl border border-border/50 bg-card p-8 text-foreground">
            <h3 className="mb-8 text-center font-bold text-2xl">
              <Droplets className="mr-2 inline h-6 w-6" />
              Su Kaynakları ve Hidrasyon
            </h3>

            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <h4 className="mb-4 font-semibold text-primary text-xl">
                  <CheckCircle className="mr-2 inline h-5 w-5" />
                  İyi Hidrasyon Kaynakları
                </h4>
                <div className="space-y-3">
                  {[
                    {
                      source: "Saf Su",
                      percent: 100,
                      desc: "En iyi hidrasyon kaynağı",
                    },
                    {
                      source: "Bitki Çayları",
                      percent: 95,
                      desc: "Kafeinsiz çaylar (papatya, nane)",
                    },
                    {
                      source: "Meyve Suları (%100)",
                      percent: 85,
                      desc: "Doğal meyve suları (seyreltilmiş)",
                    },
                    {
                      source: "Süt",
                      percent: 87,
                      desc: "Protein ve elektrolit içerir",
                    },
                    {
                      source: "Su Açısından Zengin Meyveler",
                      percent: 80,
                      desc: "Karpuz, kavun, portakal",
                    },
                    {
                      source: "Sebzeler",
                      percent: 75,
                      desc: "Salatalık, domates, marul",
                    },
                  ].map((item) => (
                    <div className="flex items-center gap-4 rounded-lg bg-card p-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                        <span className="font-bold text-primary text-xs">
                          {item.percent}%
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{item.source}</div>
                        <div className="text-muted-foreground text-xs">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="mb-4 font-semibold text-destructive text-xl">
                  <XCircle className="mr-2 inline h-5 w-5" />
                  Dehidrasyon Yapan İçecekler
                </h4>
                <div className="space-y-3">
                  {[
                    {
                      source: "Alkol",
                      effect: "Diüretik",
                      desc: "Su kaybını artırır",
                    },
                    {
                      source: "Kafeinli İçecekler",
                      effect: "Hafif Diüretik",
                      desc: "Kahve, çay, enerji içeceği",
                    },
                    {
                      source: "Gazlı İçecekler",
                      effect: "Şeker Yüklü",
                      desc: "Ekstra su ihtiyacı yaratır",
                    },
                    {
                      source: "Tuzlu İçecekler",
                      effect: "Elektrolit Bozucu",
                      desc: "Sodyum dengesini bozar",
                    },
                  ].map((item) => (
                    <div className="flex items-center gap-4 rounded-lg bg-card p-3 ">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                        <XCircle className="h-6 w-6 text-destructive" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{item.source}</div>
                        <div className="text-destructive text-xs">
                          {item.effect}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Hydration Tips */}
          <div className="rounded-2xl border border-border/50 bg-card p-8 text-foreground">
            <h3 className="mb-6 flex items-center gap-2 font-bold text-2xl">
              <Lightbulb className="h-6 w-6" />
              Hidrasyon İpuçları
            </h3>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold">Güne Su ile Başlayın</h4>
                    <p className="text-muted-foreground text-sm">
                      Uyanır uyanmaz 500ml su için, gece kaybedilen sıvıyı
                      telafi edin
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold">
                      Su Şişenizi Yanınızda Taşıyın
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Görsel hatırlatıcı olarak her zaman su bulundurun
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold">Alarm Kurun</h4>
                    <p className="text-muted-foreground text-sm">
                      Her 2 saatte bir su içmek için hatırlatıcı ayarlayın
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">
                    4
                  </div>
                  <div>
                    <h4 className="font-semibold">Yemeklerden Önce Su İçin</h4>
                    <p className="text-muted-foreground text-sm">
                      Her öğünden 30 dakika önce su içme alışkanlığı edinin
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">
                    5
                  </div>
                  <div>
                    <h4 className="font-semibold">
                      İdrar Rengini Kontrol Edin
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Açık sarı renk optimal hidrasyonu gösterir
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">
                    6
                  </div>
                  <div>
                    <h4 className="font-semibold">
                      Egzersiz Sırasında Su İçin
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Her 15-20 dakikada bir 150-250ml su alın
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">
                    7
                  </div>
                  <div>
                    <h4 className="font-semibold">Su İçmeyi Lezzetli Yapın</h4>
                    <p className="text-muted-foreground text-sm">
                      Limon, nane, salatalık ekleyerek aromalı su yapın
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">
                    8
                  </div>
                  <div>
                    <h4 className="font-semibold">Elektronik Takip Edin</h4>
                    <p className="text-muted-foreground text-sm">
                      Su içme uygulamaları veya akıllı şişeler kullanın
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Special Situations */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <Baby className="h-8 w-8 text-accent-foreground" />
                </div>
                <h3 className="font-bold text-accent-foreground">Hamilelik</h3>
              </div>
              <div className="space-y-2 text-sm">
                <p>Günlük su ihtiyacı %30 artar</p>
                <div className="font-semibold text-accent-foreground">
                  ~2.3L/gün
                </div>
                <p className="text-muted-foreground text-xs">
                  Kan hacmi artışı ve bebek için ekstra sıvı
                </p>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <Baby className="h-8 w-8 text-secondary-foreground" />
                </div>
                <h3 className="font-bold text-secondary-foreground">Emzirme</h3>
              </div>
              <div className="space-y-2 text-sm">
                <p>Günlük su ihtiyacı %60 artar</p>
                <div className="font-semibold text-secondary-foreground">
                  ~3.1L/gün
                </div>
                <p className="text-muted-foreground text-xs">
                  Süt üretimi için yoğun sıvı ihtiyacı
                </p>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <Activity className="h-8 w-8 text-secondary-foreground" />
                </div>
                <h3 className="font-bold text-secondary-foreground">Sporcu</h3>
              </div>
              <div className="space-y-2 text-sm">
                <p>Aktivite seviyesine göre %20-80 artış</p>
                <div className="font-semibold text-secondary-foreground">
                  2.5-4.5L/gün
                </div>
                <p className="text-muted-foreground text-xs">
                  Terleme ile kayıp telafisi
                </p>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <Thermometer className="h-8 w-8 text-secondary-foreground" />
                </div>
                <h3 className="font-bold text-secondary-foreground">
                  Hastalık
                </h3>
              </div>
              <div className="space-y-2 text-sm">
                <p>Ateş, kusma durumlarında %40 artış</p>
                <div className="font-semibold text-secondary-foreground">
                  3.5L+/gün
                </div>
                <p className="text-muted-foreground text-xs">
                  Sıvı kaybını telafi etmek
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
